<form [formGroup]="form.formGroup">

    <app-influx-frac-depth-base
        #influxFracDepthBase
        [form]="form"
        [influxDepthRange]="influxDepthRange"
        [tubulars]="tubulars"
        [fracPressures]="fracPressures"
        [porePressures]="porePressures"
        [userUnits]="userUnits"
        [currentString]="currentString"
        [fractureDepthDefault]="fractureDepthDefault"
    ></app-influx-frac-depth-base>

    <div class="flex gap-2 flex-row m-2">
        <p-fieldset legend="Circulation Profile" class="w-1/2">
            <div class="flex flex-col space-y-2">
                <app-uom-input
                    label="Influx Depth"
                    [unitLabel]="influxFracDepthBase.lengthUnitLabel + ' MD'"
                    [formControlName]="form.formControlNames.influxDepth"
                    [control]="'influxDepth'"
                    [default]="influxDepthRange.max"
                    [min]="influxDepthRange.min"
                    [max]="influxDepthRange.max"
                    title="Allowed range: {{ influxDepthRange.min }} - {{ influxDepthRange.max }} {{ influxFracDepthBase.lengthUnitLabel }}"
                    (focusout)="influxFracDepthBase.influxDepthChange(); getPercent();"
                />

                <div>
                    <p-checkbox
                        size="small"
                        binary="true"
                        [formControlName]="form.formControlNames.useGasGravity"
                        (onChange)="influxFracDepthBase.gasGravityChange()"
                        inputId="useGasGravity"
                    ></p-checkbox>
                    <label class="ml-2" for="useGasGravity">Use Gas Gravity</label>
                </div>

                <div [style.display]="!form.formGroup.controls.useGasGravity.value ? 'block' : 'none'">
                    <app-uom-input
                        label="Gas Gradient"
                        [unitLabel]="influxFracDepthBase.gasGradientLabel"
                        [formControlName]="form.formControlNames.gasGradient"
                        [control]="'gasGradient'"
                        [default]="influxFracDepthBase.gasGradientDefault"
                        [min]="influxFracDepthBase.gasGradientValidation.min"
                        [max]="influxFracDepthBase.gasGradientValidation.max"
                        title="Allowed range: {{ influxFracDepthBase.gasGradientValidation.min }} - {{ influxFracDepthBase.gasGradientValidation.max }} {{ influxFracDepthBase.gasGradientLabel }}"
                    />
                </div>

                <div [style.display]="form.formGroup.controls.useGasGravity.value ? 'block' : 'none'">
                    <app-uom-input
                        label="Gas Gravity"
                        [formControlName]="form.formControlNames.gasGravity"
                        [control]="'gasGravity'"
                        [default]="0.6"
                        [min]="0"
                        [max]="2"
                    />
                </div>

                <app-uom-input
                    label="Mud/Gas Interface"
                    [unitLabel]="influxFracDepthBase.lengthUnitLabel + ' MD'"
                    [formControlName]="form.formControlNames.mudGasInterface"
                    [control]="'mudGasInterface'"
                    [default]="(wellheadDepth + influxDepthRange.max) / 2"
                    [min]="influxFracDepthBase.mudGasInterfaceMin"
                    [max]="influxFracDepthBase.fracDepthsRange.max"
                    (focusout)="getPercent()"
                    title="Allowed range: {{ influxFracDepthBase.mudGasInterfaceMin }} - {{ influxFracDepthBase.fracDepthsRange.max }} {{ influxFracDepthBase.lengthUnitLabel }}"
                />

                <div class="flex flex-col">
                    <label>{{ percentLabel }}</label>
                </div>

                <app-uom-input
                    label="Fluid Density"
                    [unitLabel]="influxFracDepthBase.densityLabel"
                    [formControlName]="form.formControlNames.fluidDensity"
                    [control]="'fluidDensity'"
                    [default]="nextStringMudWeight"
                    [min]="influxFracDepthBase.densityValidation.min"
                    [max]="influxFracDepthBase.densityValidation.max"
                    title="Allowed range: {{ influxFracDepthBase.densityValidation.min }} - {{ influxFracDepthBase.densityValidation.max }} {{ influxFracDepthBase.densityLabel }}"
                />

                <app-uom-input
                    label="Pore Pressure"
                    [unitLabel]="influxFracDepthBase.densityLabel"
                    [formControlName]="form.formControlNames.porePressure"
                    [control]="'porePressure'"
                    [min]="influxFracDepthBase.densityValidation.min"
                    [max]="influxFracDepthBase.densityValidation.max"
                    (focusout)="influxFracDepthBase.porePressureChange()"
                />
            </div>
        </p-fieldset>

        <p-fieldset legend="Fracture Pressure" class="w-1/2">
            <div class="flex flex-col space-y-2">
                <div>
                    <p-checkbox
                        size="small"
                        binary="true"
                        [formControlName]="form.formControlNames.limitToFracInOpenHole"
                        inputId="limitToFracInOpenHole"
                        (onChange)="limitToFracChange()"
                    ></p-checkbox>
                    <label class="ml-2" for="limitToFracInOpenHole">Limit to Frac in Open Hole</label>
                </div>

                <app-uom-input
                    label="Fracture Depth"
                    [unitLabel]="influxFracDepthBase.lengthUnitLabel + ' MD'"
                    [formControlName]="form.formControlNames.fractureDepth"
                    [control]="'fractureDepth'"
                    [default]="influxFracDepthBase.fractureDepthDefault"
                    [min]="influxFracDepthBase.fracDepthsRange.min"
                    [max]="influxFracDepthBase.fracDepthsRange.max"
                    title="Allowed range: {{ influxFracDepthBase.fracDepthsRange.min }} - {{ influxFracDepthBase.fracDepthsRange.max }} {{ influxFracDepthBase.lengthUnitLabel }}"
                    [class.pointer-events-none]="limitToFracInOpenHoleDisabled"
                    [class.opacity-50]="limitToFracInOpenHoleDisabled"
                    (focusout)="influxFracDepthBase.fractureDepthChange()"
                />

                <app-uom-input
                    label="Fracture Gradient"
                    [unitLabel]="influxFracDepthBase.densityLabel"
                    [formControlName]="form.formControlNames.fractureGradient"
                    [control]="'fractureGradient'"
                    [default]="influxFracDepthBase.defaultFracGradient"
                    [min]="influxFracDepthBase.fractureGradientValidation.min"
                    [max]="influxFracDepthBase.fractureGradientValidation.max"
                    title="Allowed range: {{ influxFracDepthBase.fractureGradientValidation.min }} - {{ influxFracDepthBase.fractureGradientValidation.max }} {{ influxFracDepthBase.densityLabel }}"
                    [class.pointer-events-none]="limitToFracInOpenHoleDisabled"
                    [class.opacity-50]="limitToFracInOpenHoleDisabled"
                    (focusout)="influxFracDepthBase.fractureGradientChange()"
                />

                <app-uom-input
                    label="Fracture Margin"
                    [unitLabel]="influxFracDepthBase.densityLabel"
                    [formControlName]="form.formControlNames.fractureMarginOfError"
                    [control]="'fractureMarginOfError'"
                    [default]="'0'"
                    [min]="influxFracDepthBase.marginOfErrorValidation.min"
                    [max]="influxFracDepthBase.marginOfErrorValidation.max"
                    title="Allowed range: {{ influxFracDepthBase.marginOfErrorValidation.min }} - {{ influxFracDepthBase.marginOfErrorValidation.max }} {{ influxFracDepthBase.densityLabel }}"
                    [class.pointer-events-none]="limitToFracInOpenHoleDisabled"
                    [class.opacity-50]="limitToFracInOpenHoleDisabled"
                />

            </div>
        </p-fieldset>
    </div>

</form>